import { useMutation, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { compressImage, mapLimit } from "@/lib/compressImage";

// Statuses where a Back Office upload is the CUSTOMER's photos (forwarded from
// email) rather than a packing-station photo.
export const CUSTOMER_PHOTO_STATUSES = ["pre_order", "lead", "abandoned", "pending", "advance"];

export function uploadKindFor(order, user) {
  const bo = ["back_office", "super_admin"].includes(user?.role);
  return bo && CUSTOMER_PHOTO_STATUSES.includes(order?.status) ? "customer" : "packing";
}

// Compress on the device, upload 3 at a time straight into the order's Drive
// folder (created on first upload when missing). Shared by the Drive tab, the
// packing camera and the Pre Orders "Upload Photos" button.
export function useDriveUpload(orderId, { kind = "packing", onDone } = {}) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (files) => {
      const list = Array.from(files);
      const toastId = toast.loading(`📸 Preparing ${list.length} photo${list.length === 1 ? "" : "s"}…`);
      try {
        let done = 0;
        return await mapLimit(list, 3, async (f) => {
          const small = await compressImage(f);
          const fd = new FormData();
          fd.append("file", small);
          const res = await api.post(`/orders/${orderId}/drive-upload?kind=${kind}`, fd, {
            headers: { "Content-Type": "multipart/form-data" }, timeout: 120000,
          });
          done += 1;
          toast.loading(`📸 Uploading ${done}/${list.length}…`, { id: toastId });
          return res.data;
        });
      } finally {
        toast.dismiss(toastId);
      }
    },
    onSuccess: (results) => {
      qc.invalidateQueries({ queryKey: ["order", orderId] });
      qc.invalidateQueries({ queryKey: ["drive-photos", orderId] });
      qc.invalidateQueries({ queryKey: ["orders"] });
      toast.success(`📸 Uploaded ${results.length} photo${results.length === 1 ? "" : "s"} to Drive${kind === "customer" ? " · marked Photos Received" : ""}`);
      onDone?.(results);
    },
    onError: (e) => toast.error(e?.response?.data?.detail || "Upload failed"),
  });
}
